import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Seo } from "@/components/Seo";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { STORE_ADDRESS, STORE_PHONE, STORE_HOURS } from "@/lib/brand";
import { toast } from "sonner";
import { LifeBuoy, Mail, Phone, MapPin, Clock, CheckCircle2, Loader2, ChevronLeft } from "lucide-react";
import { mpLead } from "@/lib/metaPixel";

const ROLE_OPTIONS = [
  { value: "student", label: "Student" },
  { value: "parent", label: "Parent" },
  { value: "guardian", label: "Guardian" },
  { value: "other", label: "Other" },
];

const CATEGORY_OPTIONS = [
  { value: "account", label: "Account & Sign-in" },
  { value: "website-bug", label: "Website Bug" },
  { value: "order-payment", label: "Order / Payment" },
  { value: "ai-tryon", label: "AI Try-On" },
  { value: "family-account", label: "Family / Parent Account" },
  { value: "school-verification", label: "School Verification" },
  { value: "shipping-returns", label: "Shipping & Returns" },
  { value: "other", label: "Other" },
];

const inputCls =
  "w-full bg-ivory border border-cream rounded-sm px-3.5 py-3 font-body text-[15px] text-ink placeholder:text-ink/40 outline-none focus:border-rosemetal transition-colors";

export default function Support() {
  const { user } = useAuth();
  const [status, setStatus] = useState("idle"); // idle | loading | success
  const [ticketId, setTicketId] = useState("");
  const [form, setForm] = useState({
    name: user?.name || "",
    email: user?.email || "",
    role: user?.role === "parent" ? "parent" : "student",
    category: "account",
    subject: "",
    message: "",
    order_id: "",
    school_name: "",
    website: "", // honeypot
  });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.subject.trim() || form.message.trim().length < 10) {
      toast.error("Please fill in your name, email, subject, and a message of at least 10 characters.");
      return;
    }
    setStatus("loading");
    try {
      const { data } = await api.supportTicket(form);
      setTicketId(data.ticket_id);
      setStatus("success");
      try {
        window.posthog?.capture("support_ticket_submit", {
          role: form.role,
          category: form.category,
        });
      } catch {
        /* noop */
      }
      // Meta Pixel — Lead
      try { mpLead({ source: "support_ticket" }); } catch { /* noop */ }
    } catch (err) {
      setStatus("idle");
      toast.error(errMsg(err));
    }
  };

  const reset = () => {
    setStatus("idle");
    setTicketId("");
    setForm({
      name: user?.name || "",
      email: user?.email || "",
      role: user?.role === "parent" ? "parent" : "student",
      category: "account",
      subject: "",
      message: "",
      order_id: "",
      school_name: "",
      website: "",
    });
  };

  return (
    <div data-testid="support-page" className="min-h-screen bg-ivory">
      <Seo
        title="Support"
        description="Get help from a real human at Heather's Boutique. Students and parents can submit account, order, or website issues and hear back within one business day."
        path="/support"
      />

      <div className="max-w-[1100px] mx-auto px-5 sm:px-8 pt-16 pb-24">
        <Link
          to="/"
          data-testid="support-back"
          className="inline-flex items-center gap-1.5 font-label text-[10px] tracking-widest text-ink/60 hover:text-rosemetal mb-8"
        >
          <ChevronLeft className="w-3 h-3" /> BACK TO HEATHER'S
        </Link>

        <motion.header
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-12"
        >
          <p className="font-label text-[10px] tracking-[0.3em] text-rosemetal mb-3 inline-flex items-center gap-2">
            <LifeBuoy className="w-3.5 h-3.5" /> HEATHER'S BOUTIQUE · SUPPORT
          </p>
          <h1 className="font-display text-4xl md:text-5xl leading-tight text-ink">Talk to a real human.</h1>
          <p className="font-body text-base md:text-lg text-ink/70 mt-4 max-w-2xl leading-relaxed">
            Something not working right? Locked out of your account? Payment weirdness? Send us a note and a real
            person on our team will reply within one business day. Faster if you call.
          </p>
        </motion.header>

        <div className="grid lg:grid-cols-[1fr_320px] gap-10">
          {/* --------------- FORM --------------- */}
          <div>
            {status === "success" ? (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                data-testid="support-success"
                className="border border-cream bg-cream/40 rounded-sm p-8"
              >
                <span className="inline-flex w-14 h-14 rounded-full bg-rosemetal/10 items-center justify-center mb-5">
                  <CheckCircle2 className="w-7 h-7 text-rosemetal" strokeWidth={1.5} />
                </span>
                <p className="font-label text-[10px] tracking-widest text-rosemetal mb-2">TICKET RECEIVED</p>
                <h2 className="font-display text-3xl md:text-4xl text-ink leading-tight">
                  Thanks — we've got you.
                </h2>
                <p className="font-body text-[15px] text-ink/70 mt-4 leading-relaxed">
                  A real human on the Heather's team will reply to <b>{form.email}</b> within one business day. If it's
                  urgent, call or text the store at <a href={`tel:${STORE_PHONE.replace(/\D/g, "")}`} className="text-rosemetal hover:text-ink">{STORE_PHONE}</a>.
                </p>
                <div className="mt-6 flex flex-wrap items-center gap-4">
                  <span className="font-label text-[10px] tracking-widest text-ink/50">TICKET ID</span>
                  <span data-testid="support-ticket-id" className="font-mono text-sm px-3 py-1.5 bg-ink text-cream rounded-sm">{ticketId}</span>
                </div>
                <button
                  onClick={reset}
                  data-testid="support-new-ticket"
                  className="mt-8 inline-flex items-center gap-2 font-label text-[10px] tracking-widest text-rosemetal hover:text-ink"
                >
                  Submit another ticket →
                </button>
              </motion.div>
            ) : (
              <form onSubmit={submit} data-testid="support-form" className="space-y-5">
                {/* Role toggle */}
                <div>
                  <label className="block font-label text-[10px] tracking-widest text-ink/60 mb-2">I'M A</label>
                  <div className="flex flex-wrap gap-2">
                    {ROLE_OPTIONS.map((r) => (
                      <button
                        key={r.value}
                        type="button"
                        onClick={() => setForm((f) => ({ ...f, role: r.value }))}
                        data-testid={`support-role-${r.value}`}
                        className={`px-4 py-2 rounded-full font-label text-[10px] tracking-widest border transition-colors ${
                          form.role === r.value
                            ? "bg-ink text-cream border-ink"
                            : "border-cream text-ink hover:border-rosemetal hover:text-rosemetal"
                        }`}
                      >
                        {r.label.toUpperCase()}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block font-label text-[10px] tracking-widest text-ink/60 mb-2">FULL NAME</label>
                    <input
                      required
                      value={form.name}
                      onChange={set("name")}
                      placeholder="Riley Thompson"
                      data-testid="support-name"
                      className={inputCls}
                    />
                  </div>
                  <div>
                    <label className="block font-label text-[10px] tracking-widest text-ink/60 mb-2">EMAIL</label>
                    <input
                      required
                      type="email"
                      value={form.email}
                      onChange={set("email")}
                      placeholder="you@school.edu"
                      data-testid="support-email"
                      className={inputCls}
                    />
                  </div>
                </div>

                <div>
                  <label className="block font-label text-[10px] tracking-widest text-ink/60 mb-2">WHAT'S GOING ON</label>
                  <select
                    value={form.category}
                    onChange={set("category")}
                    data-testid="support-category"
                    className={inputCls}
                  >
                    {CATEGORY_OPTIONS.map((c) => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block font-label text-[10px] tracking-widest text-ink/60 mb-2">SUBJECT</label>
                  <input
                    required
                    value={form.subject}
                    onChange={set("subject")}
                    placeholder="e.g. Can't verify my school email"
                    data-testid="support-subject"
                    className={inputCls}
                  />
                </div>

                <div>
                  <label className="block font-label text-[10px] tracking-widest text-ink/60 mb-2">TELL US WHAT HAPPENED</label>
                  <textarea
                    required
                    value={form.message}
                    onChange={set("message")}
                    minLength={10}
                    rows={7}
                    placeholder="Describe the issue in your own words. Include steps you tried, error messages you saw, and anything else that helps us track it down."
                    data-testid="support-message"
                    className={`${inputCls} resize-y min-h-[150px]`}
                  />
                  <p className="font-label text-[9px] tracking-widest text-ink/40 mt-2">MIN 10 CHARACTERS</p>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block font-label text-[10px] tracking-widest text-ink/60 mb-2">ORDER ID <span className="text-ink/40">· OPTIONAL</span></label>
                    <input
                      value={form.order_id}
                      onChange={set("order_id")}
                      placeholder="If this is about an order"
                      data-testid="support-order"
                      className={inputCls}
                    />
                  </div>
                  <div>
                    <label className="block font-label text-[10px] tracking-widest text-ink/60 mb-2">SCHOOL <span className="text-ink/40">· OPTIONAL</span></label>
                    <input
                      value={form.school_name}
                      onChange={set("school_name")}
                      placeholder="e.g. Oconomowoc High School"
                      data-testid="support-school"
                      className={inputCls}
                    />
                  </div>
                </div>

                {/* Honeypot — hidden from users, catches bots */}
                <input
                  type="text"
                  name="website"
                  tabIndex={-1}
                  autoComplete="off"
                  value={form.website}
                  onChange={set("website")}
                  aria-hidden="true"
                  className="hidden"
                />

                <button
                  type="submit"
                  disabled={status === "loading"}
                  data-testid="support-submit"
                  className="inline-flex items-center gap-2 bg-ink text-cream px-8 py-4 font-label text-[10px] tracking-[0.3em] rounded-sm hover:bg-rosemetal transition-colors disabled:opacity-60"
                >
                  {status === "loading" ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" /> SENDING…
                    </>
                  ) : (
                    <>SUBMIT TICKET →</>
                  )}
                </button>
                <p className="font-label text-[9px] tracking-widest text-ink/40">
                  YOUR MESSAGE GOES DIRECTLY TO WEBMASTER@ AND SUPPORT@HEATHERSBOUTIQUE.COM
                </p>
              </form>
            )}
          </div>

          {/* --------------- SIDEBAR --------------- */}
          <aside className="space-y-5">
            <div className="border border-cream rounded-sm p-6 bg-cream/40">
              <p className="font-label text-[10px] tracking-widest text-rosemetal mb-2">FASTER OPTIONS</p>
              <h3 className="font-display text-2xl text-ink mb-4">Prefer to skip the form?</h3>
              <ul className="space-y-4 font-body text-[14px] text-ink/80">
                <li className="flex items-start gap-3">
                  <Phone className="w-4 h-4 text-rosemetal mt-0.5" strokeWidth={1.75} />
                  <div>
                    <a href={`tel:${STORE_PHONE.replace(/\D/g, "")}`} className="font-display text-xl text-ink hover:text-rosemetal">{STORE_PHONE}</a>
                    <p className="font-label text-[10px] tracking-widest text-ink/50 mt-1">CALL OR TEXT</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Mail className="w-4 h-4 text-rosemetal mt-0.5" strokeWidth={1.75} />
                  <div>
                    <a href="mailto:support@heathersboutique.com" className="hover:text-rosemetal">support@heathersboutique.com</a>
                    <p className="font-label text-[10px] tracking-widest text-ink/50 mt-1">DIRECT EMAIL</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Clock className="w-4 h-4 text-rosemetal mt-0.5" strokeWidth={1.75} />
                  <div className="text-[13px]">
                    <div>{STORE_HOURS}</div>
                    <p className="font-label text-[10px] tracking-widest text-ink/50 mt-1">STORE HOURS · CENTRAL TIME</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <MapPin className="w-4 h-4 text-rosemetal mt-0.5" strokeWidth={1.75} />
                  <div className="text-[13px]">{STORE_ADDRESS}</div>
                </li>
              </ul>
            </div>

            <div className="border border-cream rounded-sm p-6">
              <p className="font-label text-[10px] tracking-widest text-rosemetal mb-3">MIGHT ALSO HELP</p>
              <ul className="space-y-2 font-body text-sm">
                <li><Link to="/returns" className="text-ink hover:text-rosemetal">Returns & refunds policy →</Link></li>
                <li><Link to="/shipping" className="text-ink hover:text-rosemetal">Shipping policy →</Link></li>
                <li><Link to="/parents" className="text-ink hover:text-rosemetal">Parent accounts →</Link></li>
                <li><Link to="/privacy" className="text-ink hover:text-rosemetal">Privacy policy →</Link></li>
                <li><Link to="/accessibility" className="text-ink hover:text-rosemetal">Accessibility →</Link></li>
              </ul>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
